import React from "react";
import { Badge, Banner, Button, Card, Layout } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import StorePageFrame from "../userCustomDashboard/emLayout/StorePageFrame";
import ClientDetails from "../troubleshooting/clientDetails";
import SyncEngineHealthPanel from "../support/SyncEngineHealthPanel";
import {
  formatCount,
  formatDateTime,
  readableStatus,
  statusTone,
  storeDomain,
  storeName,
} from "../support/supportFormatters";
import { useStoreWorkspace } from "./StoreWorkspaceContext";
import WorkspaceState from "./WorkspaceState";
import { workspaceModel } from "./workspaceModel";
import "./style.css";

const StoreActions = () => {
  const navigate = useNavigate();
  const workspace = useStoreWorkspace();
  const model = workspaceModel(workspace.data, workspace.shopId);
  const { capabilities, connections, disputes, onboarding, pipeline, lifecycle, store } = model;
  const canWrite = capabilities?.canWrite === true;
  const canAdmin = capabilities?.canAdmin === true;
  const accessLevel = canAdmin ? "admin" : canWrite ? "write" : "read";
  const paypal = connections.paypal || {};
  const merchantScreen =
    String(onboarding.status || "").toUpperCase() === "UNTRACKED"
      ? "Legacy dashboard / onboarding not tracked"
      : String(onboarding.merchantScreen || "UNKNOWN").toUpperCase() === "ONBOARDING"
      ? `Onboarding · step ${onboarding.currentStep || 1} of 3`
      : readableStatus(onboarding.merchantScreen, "Unknown");

  return (
    <StorePageFrame title="Support actions" maxWidth="1400px">
      <WorkspaceState
        loading={workspace.loading}
        refreshing={workspace.refreshing}
        error={workspace.error}
        hasData={Boolean(workspace.data)}
        onRetry={() => workspace.refresh()}
      >
        <Card>
          <div className="store-support-card">
            <div className="store-support-heading store-support-heading--action">
              <div>
                <h2>Confirm the target and current evidence</h2>
                <p>
                  Every operation below is scoped to Shopify store ID {store.id || workspace.shopId}.
                  Refresh this evidence before changing merchant state.
                </p>
              </div>
              <Button onClick={() => workspace.refresh()}>Refresh evidence</Button>
            </div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <div className="store-support-definition">
                <dt>Store</dt>
                <dd>{storeName(store)}</dd>
                <small>{storeDomain(store)} · ID {store.id || workspace.shopId}</small>
              </div>
              <div className="store-support-definition">
                <dt>PayPal connection</dt>
                <dd><Badge status={statusTone(paypal.status)}>{readableStatus(paypal.status)}</Badge></dd>
                <small>{formatCount(paypal.activeAccounts)} usable account(s)</small>
              </div>
              <div className="store-support-definition">
                <dt>Merchant screen</dt>
                <dd>{merchantScreen}</dd>
                <small>Onboarding {readableStatus(onboarding.status)}</small>
              </div>
              <div className="store-support-definition">
                <dt>Sync queue</dt>
                <dd>{formatCount(pipeline.queued)} queued · {formatCount(pipeline.processing)} processing</dd>
                <small>Last success {formatDateTime(lifecycle.lastSyncedAt || pipeline.lastSuccessfulAt)}</small>
              </div>
              <div className="store-support-definition">
                <dt>Disputes</dt>
                <dd>{formatCount(disputes.attention)} need attention</dd>
                <small>{formatCount(disputes.open)} open · sync {readableStatus(disputes.syncStatus)}</small>
              </div>
              <div className="store-support-definition">
                <dt>Support permission</dt>
                <dd><Badge status={canAdmin ? "warning" : canWrite ? "info" : "new"}>{readableStatus(accessLevel)}</Badge></dd>
                <small>Actions remain confirmation-gated.</small>
              </div>
            </dl>
            <div className="store-support-hero__actions store-support-actions-links">
              <Button onClick={() => navigate(`/store/${workspace.shopId}/events`)}>Open diagnostics</Button>
              <Button onClick={() => navigate(`/store/${workspace.shopId}/disputes`)}>Review disputes</Button>
              <Button onClick={() => navigate(`/store/${workspace.shopId}/configuration`)}>Inspect connections</Button>
            </div>
          </div>
        </Card>
        <SyncEngineHealthPanel shopId={workspace.shopId} />
        <Banner
          title={
            accessLevel === "admin"
              ? "Guarded support operations"
              : accessLevel === "write"
              ? "Limited support operations"
              : "Read-only support access"
          }
          status={canAdmin ? "warning" : "info"}
        >
          <p>
            {accessLevel === "admin"
              ? "Review the target store and confirmation summary before running any operation. Completed actions are not automatically reversible."
              : accessLevel === "write"
              ? "This identity can refresh store data, but reindex, cancellation, and global cache operations require support administrator access."
              : "This identity can investigate stores but cannot run state-changing support actions. Ask a PalSync administrator to grant support write access."}
          </p>
        </Banner>
        <Layout>
          <ClientDetails
            actionsOnly
            actionsEnabled={canWrite}
            adminActionsEnabled={canAdmin}
          />
        </Layout>
      </WorkspaceState>
    </StorePageFrame>
  );
};

export default StoreActions;
